import { AnimatePresence, motion } from 'motion/react';
import { api, type Contenedor, type Orden } from '../api';
import { useApi, VIVO } from '../lib/useApi';
import { useSesion } from '../lib/sesion';
import { Cargando, DetalleOrden, Encabezado, EstadoChip, Icono, Kpi, PanelError, Paginador, usePaginado, Vacio } from '../components/ui';
import { c, enlace, fechaImpresa, mono, tarjeta } from '../theme';
import type { Vista } from '../components/Layout';
import { aparecer, fila as entrada } from '../components/movimiento';

/** Los paneles del inicio son cortos: cinco renglones y el resto se pasea. */
const POR_PANEL = 5;

function Panel({
  titulo,
  accion,
  onAccion,
  children,
}: {
  titulo: string;
  accion: string;
  onAccion: () => void;
  children: React.ReactNode;
}) {
  return (
    <motion.div {...aparecer} style={{ ...tarjeta, overflow: 'hidden', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: c.texto, flex: 1 }}>{titulo}</span>
        <button
          type="button"
          onClick={onAccion}
          style={{
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid transparent',
            background: 'transparent',
            color: c.verde,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {accion}
        </button>
      </div>
      {children}
    </motion.div>
  );
}

export default function Dashboard({ onVista }: { onVista: (v: Vista, id?: string) => void }) {
  // El inicio es la pantalla que queda abierta: se mantiene sola, así una orden
  // que pide ZF aparece sin que nadie recargue.
  const kpis = useApi(() => api.dashboard(), [], VIVO.normal);
  const contenedores = useApi(() => api.contenedores(), [], VIVO.normal);
  const ordenes = useApi(() => api.ordenes(), [], VIVO.rapido);

  const { vistas } = useSesion();
  const puedeVerFrac = vistas.includes('frac');
  const puedeVerContenedor = vistas.includes('contenedor');

  // Los dos paneles se pasean de a cinco en vez de mostrar los cuatro últimos
  // y esconder el resto: desde acá se entra a una orden, así que hay que poder
  // llegar a la de anteayer sin irse a otra pantalla.
  const ingresos = usePaginado<Contenedor>(contenedores.datos ?? [], POR_PANEL);
  const ots = usePaginado<Orden>(ordenes.datos ?? [], POR_PANEL);

  // Cada consulta muestra su error en su propio lugar: que no contesten los
  // indicadores no es motivo para esconder las órdenes que sí llegaron.
  const d = kpis.datos;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Escanear y buscar están en el encabezado, igual en todas las pantallas. */}
      <Encabezado bajada="Stock, ingresos y órdenes de trabajo al día de hoy." tamano={38} />

      {kpis.error && !d ? (
        <PanelError mensaje={kpis.error} onReintentar={kpis.recargar} />
      ) : kpis.cargando && !d ? (
        <Cargando texto="Calculando indicadores…" />
      ) : (
        d && (
          // Cuatro indicadores: de a uno, de a dos o los cuatro en fila. Con
          // auto-fit quedaban tres arriba y uno suelto abajo.
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*>*]:h-full xl:grid-cols-4">
            <motion.div {...entrada(0)}>
            <Kpi
              rotulo="Pallets en depósito"
              valor={d.palletsEnDeposito}
              pie={
                d.posiciones === 0
                  ? 'todavía no hay ubicaciones cargadas'
                  : `en ${d.sectores} ${d.sectores === 1 ? 'sector' : 'sectores'} · ${d.ocupacion}% de ocupación`
              }
            />
            </motion.div>
            <motion.div {...entrada(1)}>
            <Kpi
              rotulo="Contenedores en tránsito"
              valor={d.contenedoresEnTransito}
              pie={d.proximoArribo ? `próximo arribo ${d.proximoArribo}` : 'sin arribos programados'}
            />
            </motion.div>
            <motion.div {...entrada(2)}>
            <Kpi
              rotulo="OT pendientes"
              valor={d.otAbiertas}
              pie={
                d.otEnArmado > 0
                  ? `${d.otEnArmado} ${d.otEnArmado === 1 ? 'espera' : 'esperan'} armado de capachos`
                  : 'ninguna en armado'
              }
              pieColor={d.otEnArmado > 0 ? c.ambar : undefined}
            />
            </motion.div>
            <motion.div {...entrada(3)}>
            {d.palletsSinUbicar > 0 ? (
              // Clickeable: es la tarea del día, y de acá se sale derecho a
              // resolverla en vez de pasar primero por el mapa.
              <button
                type="button"
                onClick={() => onVista('ubicar')}
                title="Ubicar los pallets pendientes"
                style={{ display: 'block', width: '100%', padding: 0, border: 0, background: 'none', textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
              >
                <Kpi
                  rotulo="Sin ubicar"
                  valor={d.palletsSinUbicar}
                  pie="ingresaron y esperan posición"
                  color={c.ambar}
                  pieColor={c.ambar}
                />
              </button>
            ) : (
              <Kpi
                rotulo="Pallets en 2ª quincena"
                valor={d.palletsSegundaQuincena}
                pie="segundo bloque de cobro"
              />
            )}
            </motion.div>
          </div>
        )
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(340px,100%),1fr))', gap: 16 }}>
        <Panel titulo="Últimos ingresos" accion="Ver todo" onAccion={() => onVista('contenedores')}>
          {contenedores.error && ingresos.total === 0 ? (
            <div style={{ padding: 16 }}>
              <PanelError mensaje={contenedores.error} onReintentar={contenedores.recargar} />
            </div>
          ) : contenedores.cargando && ingresos.total === 0 ? (
            <Cargando alto={140} />
          ) : ingresos.total === 0 ? (
            <Vacio icono="local_shipping" titulo="Sin ingresos" detalle="Todavía no hay contenedores declarados." />
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
            {ingresos.visibles.map((ct, i) => (
              <motion.div
                key={ct.id}
                layout="position"
                {...entrada(i)}
                // Hasta 1280 px, código y estado arriba y el detalle abajo a lo
                // ancho: en una sola fila el detalle quedaba en una columna de
                // 60 px partido en cuatro renglones (celular), y los dos paneles
                // lado a lado de la tablet y la PC chica lo apretaban igual.
                className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 xl:flex xl:gap-3.5"
                style={{ padding: '12px 16px', borderBottom: `1px solid ${c.borde}` }}
              >
                <span
                  className="xl:min-w-[118px]"
                  style={{ fontFamily: mono, fontSize: 12.5, color: c.texto, fontWeight: 500, minWidth: 0 }}
                >
                  {puedeVerContenedor ? (
                    <button type="button" onClick={() => onVista('contenedor', ct.id)} title="Ver la ficha del contenedor" style={{ ...enlace, fontSize: 12.5 }}>
                      {ct.id}
                    </button>
                  ) : (
                    ct.id
                  )}
                </span>
                <span className="col-span-2 row-start-2 xl:flex-1" style={{ minWidth: 0, fontSize: 13, color: c.texto2 }}>
                  {ct.pallets} pallets · {ct.fecha}
                </span>
                <span className="col-start-2 row-start-1 justify-self-end">
                  <EstadoChip estado={ct.estado} />
                </span>
              </motion.div>
            ))}
            </AnimatePresence>
          )}
          <Paginador
            pagina={ingresos.pagina}
            paginas={ingresos.paginas}
            total={ingresos.total}
            desde={ingresos.desde}
            porPagina={POR_PANEL}
            que="contenedores"
            onIr={ingresos.setPagina}
          />
        </Panel>

        <Panel titulo="Órdenes de trabajo" accion="Solicitar" onAccion={() => onVista('solicitud')}>
          {ordenes.error && ots.total === 0 ? (
            <div style={{ padding: 16 }}>
              <PanelError mensaje={ordenes.error} onReintentar={ordenes.recargar} />
            </div>
          ) : ordenes.cargando && ots.total === 0 ? (
            <Cargando alto={140} />
          ) : ots.total === 0 ? (
            <Vacio icono="assignment_add" titulo="Sin órdenes" detalle="Cuando el cliente pida un egreso, aparece acá." />
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
            {ots.visibles.map((o, i) => {
              // La orden lleva a la mesa de fraccionamiento, que es donde se
              // trabaja. Al cliente de ZF no: ahí no entra, y un renglón que
              // no lleva a ninguna parte es peor que uno que no se toca.
              const fila = (
                <>
                  {/* Igual que los ingresos: en el celular el número y el
                      estado arriba, y lo que pidió abajo a todo el ancho. */}
                  <div
                    className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5 xl:flex xl:gap-3.5"
                    style={{ width: '100%' }}
                  >
                    <span
                      className="xl:min-w-[92px]"
                      style={{ fontFamily: mono, fontSize: 12.5, color: c.texto, fontWeight: 500, textAlign: 'left' }}
                    >
                      {o.id}
                    </span>
                    <span className="col-span-2 row-start-2 xl:flex-1" style={{ minWidth: 0, textAlign: 'left' }}>
                      <DetalleOrden orden={o} />
                    </span>
                    <span className="col-start-2 row-start-1 justify-self-end">
                      <EstadoChip estado={o.estado} />
                    </span>
                  </div>
                  {/* Cómo viene y de quién es. El avance es lo que se mira
                      para saber en qué anda; el mail, para saber a quién
                      contestarle. */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12, color: c.texto4 }}>
                    <span>
                      {o.entregadas} de {o.salidas} {o.unidad === 'pallet' ? 'pallets' : 'capachos'} entregados
                    </span>
                    {o.pedidaPor && <span>· {o.pedidaPor}</span>}
                    {o.entregaEstimada && <span>· Entrega estimada {fechaImpresa(o.entregaEstimada)}</span>}
                  </div>

                  {/* Lo que el cliente pidió por escrito: film, esquineros, a
                      qué hora pasa el camión. Estaba guardado y no se veía en
                      ninguna pantalla más que la del armado. */}
                  {o.observaciones && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 7,
                        fontSize: 12.5,
                        color: c.texto3,
                        textAlign: 'left',
                        textWrap: 'pretty',
                      }}
                    >
                      <Icono nombre="sticky_note_2" size={16} color={c.texto4} />
                      <span>{o.observaciones}</span>
                    </div>
                  )}
                </>
              );
              const estilo: React.CSSProperties = {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 6,
                padding: '12px 16px',
                borderBottom: `1px solid ${c.borde}`,
              };

              return puedeVerFrac ? (
                <motion.button
                  key={o.id}
                  layout="position"
                  {...entrada(i)}
                  type="button"
                  className="wms-toca"
                  onClick={() => onVista('frac', o.id)}
                  title={`Ver ${o.id} en la mesa de fraccionamiento`}
                  style={{ ...estilo, width: '100%', border: 'none', borderBottom: `1px solid ${c.borde}`, background: 'none', font: 'inherit' }}
                >
                  {fila}
                </motion.button>
              ) : (
                <motion.div key={o.id} layout="position" {...entrada(i)} style={estilo}>
                  {fila}
                </motion.div>
              );
            })}
            </AnimatePresence>
          )}
          <Paginador
            pagina={ots.pagina}
            paginas={ots.paginas}
            total={ots.total}
            desde={ots.desde}
            porPagina={POR_PANEL}
            que="órdenes"
            onIr={ots.setPagina}
          />
        </Panel>
      </div>
    </div>
  );
}
